import React from 'react';
import { Box, Typography, Avatar, alpha, useTheme, Grid, Chip, Divider } from '@mui/material';
import ConstructionIcon from '@mui/icons-material/Construction';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { StyledCard, StyledCardHeader, StyledCardContent, MetricChip, formatDisplayItem } from '@features/analysis/components/StyledComponents';
import { AIAnalysisResult } from '@features/analysis/types';

interface RenovationPotentialCardProps {
  analysis: AIAnalysisResult;
}

const RenovationPotentialCard: React.FC<RenovationPotentialCardProps> = ({ analysis }) => {
  const theme = useTheme();
  const renovationPotential = analysis.renovationPotential;

  // Helper function to get priority color
  const getPriorityColor = (priority: string) => {
    const p = priority.toLowerCase();
    if (p.includes('high') || p.includes('urgent') || p.includes('critical')) {
      return theme.palette.error.main;
    } else if (p.includes('medium') || p.includes('moderate')) {
      return theme.palette.warning.main;
    } else {
      return theme.palette.success.main;
    }
  };

  return (
    <StyledCard>
      <StyledCardHeader
        title="Renovation Potential"
        subheader="Capital improvements and value enhancement opportunities"
        avatar={
          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
            <ConstructionIcon />
          </Avatar>
        }
      />
      <StyledCardContent>
        {/* Investment Overview */}
        {(renovationPotential?.estimatedCosts || renovationPotential?.potentialValueIncrease || renovationPotential?.paybackPeriod) && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Estimated Costs */}
            {renovationPotential?.estimatedCosts && (
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(theme.palette.info.main, 0.03),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                  textAlign: 'center'
                }}>
                  <MonetizationOnIcon sx={{ color: theme.palette.info.main, fontSize: 32, mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Estimated Investment
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                    {renovationPotential.estimatedCosts.low && renovationPotential.estimatedCosts.high
                      ? `$${renovationPotential.estimatedCosts.low.toLocaleString()} - $${renovationPotential.estimatedCosts.high.toLocaleString()}`
                      : renovationPotential.estimatedCosts.low
                        ? `$${renovationPotential.estimatedCosts.low.toLocaleString()}`
                        : renovationPotential.estimatedCosts.high
                          ? `$${renovationPotential.estimatedCosts.high.toLocaleString()}`
                          : 'TBD'
                    }
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Value Increase */}
            {renovationPotential?.potentialValueIncrease && (
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(theme.palette.success.main, 0.03),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  textAlign: 'center'
                }}>
                  <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 32, mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Value Increase Potential
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                    {renovationPotential.potentialValueIncrease}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Payback Period */}
            {renovationPotential?.paybackPeriod && (
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2.5,
                  bgcolor: alpha(theme.palette.warning.main, 0.03),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                  textAlign: 'center'
                }}>
                  <AccessTimeIcon sx={{ color: theme.palette.warning.main, fontSize: 32, mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Expected Payback Period
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                    {renovationPotential.paybackPeriod}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {/* Priority Areas */}
        {renovationPotential?.priorityAreas && renovationPotential.priorityAreas.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
              <PriorityHighIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Priority Renovation Areas
            </Typography>
            <Grid container spacing={1.5}>
              {renovationPotential.priorityAreas.map((area, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box sx={{
                    p: 1.5,
                    bgcolor: alpha(getPriorityColor(area), 0.05),
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(getPriorityColor(area), 0.2)}`
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: getPriorityColor(area) }}>
                      {formatDisplayItem(area)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Recommended Updates */}
        {renovationPotential?.recommendedUpdates && renovationPotential.recommendedUpdates.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 2 }}>
              Recommended Improvements
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {renovationPotential.recommendedUpdates.map((update, index) => (
                <MetricChip
                  key={index}
                  label={formatDisplayItem(update)}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* ROI Analysis */}
        {(renovationPotential?.estimatedCosts && renovationPotential?.potentialValueIncrease) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                💡 Investment Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.4, mb: 1 }}>
                Based on the estimated costs and potential value increase, this renovation could provide:
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Investment Range</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {renovationPotential.estimatedCosts.low && renovationPotential.estimatedCosts.high
                      ? `$${renovationPotential.estimatedCosts.low.toLocaleString()} - $${renovationPotential.estimatedCosts.high.toLocaleString()}`
                      : 'Variable'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Payback Timeline</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {renovationPotential.paybackPeriod || 'To be determined'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {/* Empty State */}
        {!renovationPotential && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ConstructionIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No renovation analysis available. Include 'Property Renovation' in your focus areas for detailed improvement recommendations.
            </Typography>
          </Box>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

export default RenovationPotentialCard;